import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import { assignmentAPI } from '../services/api';
import { FormattedText } from '../utils/formatText';
import Editor from '@monaco-editor/react';
import ReportIssueButton from '../components/ReportIssueButton';

const AssignmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null); // { language, stepNumber, questions }
  const [showQuizEditModal, setShowQuizEditModal] = useState(false);
  const [showAIQuestionsModal, setShowAIQuestionsModal] = useState(false);
  const [aiQuestions, setAIQuestions] = useState(null);
  const [loadingAIQuestions, setLoadingAIQuestions] = useState(false);
  const [editingPseudocode, setEditingPseudocode] = useState(null); // { language: '', text: '' }
  const [savingPseudocode, setSavingPseudocode] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [assignmentGroups, setAssignmentGroups] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const isAdmin = user && user.role === 'admin';

  const developerLevelLabels = {
    internDeveloper: { label: 'Intern Developer', icon: '👨‍💻', color: '#48bb78' },
    juniorDeveloper: { label: 'Junior Developer', icon: '💻', color: '#667eea' },
    midLevelDeveloper: { label: 'Mid-Level Developer', icon: '🚀', color: '#fbbf24' },
    seniorDeveloper: { label: 'Senior Developer', icon: '⭐', color: '#f97316' },
    leadDeveloper: { label: 'Lead Developer', icon: '🎖️', color: '#ef4444' }
  };

  useEffect(() => {
    // Redirect non-admins
    if (user && user.role !== 'admin') {
      navigate('/admin/coding-assignments');
      return;
    }
    if (user) {
      fetchAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assignmentAPI.getAssignmentById(id);
      const assignmentData = response.data.assignment;
      setAssignment(assignmentData);
      
      // Set first language as default selected language
      if (assignmentData.languages && assignmentData.languages.length > 0) {
        setSelectedLanguage(assignmentData.languages[0]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate('/admin/coding-assignments', { state: { editAssignment: assignment } });
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${assignment.title}"? This will also delete all student submissions.`)) {
      return;
    }

    try {
      await assignmentAPI.deleteAssignment(assignment._id);
      alert('Assignment deleted successfully');
      navigate('/admin/coding-assignments');
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Failed to delete assignment: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleViewAIQuestions = async () => {
    try {
      setLoadingAIQuestions(true);
      setShowAIQuestionsModal(true);
      const response = await assignmentAPI.getAIQuestions(assignment._id);
      setAIQuestions(response.data);
    } catch (error) {
      console.error('Error fetching AI questions:', error);
      alert('Failed to load AI questions: ' + (error.response?.data?.message || error.message));
      setShowAIQuestionsModal(false);
    } finally {
      setLoadingAIQuestions(false);
    }
  };

  const handleViewGroups = async () => {
    try {
      setLoadingGroups(true);
      setShowGroupsModal(true);
      const response = await assignmentAPI.getAssignmentGroups(assignment._id);
      setAssignmentGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      alert('Failed to load groups: ' + (error.response?.data?.message || error.message));
      setShowGroupsModal(false);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleEditStep = (step, stepIndex) => {
    // Wrap starter code with markdown syntax for editing
    const starterCodeMarkdown = step.starterCode 
      ? `\`\`\`${selectedLanguage}\n${step.starterCode}\n\`\`\``
      : '';
    setEditingStep({ 
      ...step, 
      stepIndex, 
      language: selectedLanguage,
      starterCodeMarkdown // Store the full markdown for editing
    });
    setShowEditModal(true);
  };

  const handleSaveStep = async () => {
    try {
      setSaving(true);
      
      // Extract the raw code from the markdown syntax
      let extractedCode = '';
      if (editingStep.starterCodeMarkdown && editingStep.starterCodeMarkdown.trim()) {
        // Match pattern: ```language\ncode\n```
        const codeBlockMatch = editingStep.starterCodeMarkdown.match(/^```[a-zA-Z]*\n([\s\S]*?)\n```$/m);
        if (codeBlockMatch) {
          extractedCode = codeBlockMatch[1];
        } else {
          // If no valid markdown syntax, treat the whole thing as code
          extractedCode = editingStep.starterCodeMarkdown;
        }
      }
      
      // Create updated assignment with modified step
      const updatedAssignment = { ...assignment };
      const langSteps = [...updatedAssignment.algorithmByLanguage[editingStep.language].steps];
      
      // Update the specific step
      langSteps[editingStep.stepIndex] = {
        stepNumber: editingStep.stepNumber,
        title: editingStep.title,
        description: editingStep.description,
        starterCode: extractedCode,
        hints: editingStep.hints
      };
      
      updatedAssignment.algorithmByLanguage[editingStep.language].steps = langSteps;
      
      // Send update to backend
      await assignmentAPI.updateAssignment(assignment._id, {
        algorithmByLanguage: updatedAssignment.algorithmByLanguage
      });
      
      // Refresh assignment data
      await fetchAssignment();
      setShowEditModal(false);
      setEditingStep(null);
    } catch (error) {
      console.error('Error saving step:', error);
      alert('Failed to save step: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddHint = () => {
    setEditingStep({
      ...editingStep,
      hints: [...editingStep.hints, { text: '', pointPenalty: 0, allowFollowUp: true, maxFollowUps: 2 }]
    });
  };

  const handleRemoveHint = (hintIndex) => {
    setEditingStep({
      ...editingStep,
      hints: editingStep.hints.filter((_, idx) => idx !== hintIndex)
    });
  };

  const handleUpdateHint = (hintIndex, field, value) => {
    const updatedHints = [...editingStep.hints];
    updatedHints[hintIndex] = { ...updatedHints[hintIndex], [field]: value };
    setEditingStep({ ...editingStep, hints: updatedHints });
  };

  const handleDeleteStep = async (stepIndex) => {
    const stepToDelete = assignment.algorithmByLanguage[selectedLanguage].steps[stepIndex];
    
    if (!window.confirm(`Are you sure you want to delete Step ${stepToDelete.stepNumber}: "${stepToDelete.title}"?`)) {
      return;
    }

    try {
      setSaving(true);
      
      // Create updated assignment with step removed
      const updatedAssignment = { ...assignment };
      const langSteps = [...updatedAssignment.algorithmByLanguage[selectedLanguage].steps];
      
      // Remove the step
      langSteps.splice(stepIndex, 1);
      
      // Re-number remaining steps
      langSteps.forEach((step, index) => {
        step.stepNumber = index + 1;
      });
      
      updatedAssignment.algorithmByLanguage[selectedLanguage].steps = langSteps;
      
      // Send update to backend
      await assignmentAPI.updateAssignment(assignment._id, {
        algorithmByLanguage: updatedAssignment.algorithmByLanguage
      });
      
      // Refresh assignment data
      await fetchAssignment();
      alert('Step deleted successfully');
    } catch (error) {
      console.error('Error deleting step:', error);
      alert('Failed to delete step: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEditQuiz = (language, stepNumber) => {
    const questions = assignment.quizzesByLanguage?.[language]?.[stepNumber] || [];
    setEditingQuiz({
      language,
      stepNumber,
      questions: JSON.parse(JSON.stringify(questions)) // Deep copy
    });
    setShowQuizEditModal(true);
  };

  const handleSaveQuiz = async () => {
    try {
      setSaving(true);
      await assignmentAPI.updateQuizQuestions(
        assignment._id,
        editingQuiz.language,
        editingQuiz.stepNumber,
        editingQuiz.questions
      );
      await fetchAssignment();
      setShowQuizEditModal(false);
      setEditingQuiz(null);
      alert('Quiz questions updated successfully');
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert('Failed to save quiz: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuizQuestion = async (language, stepNumber, questionIndex) => {
    if (!window.confirm('Are you sure you want to delete this quiz question?')) {
      return;
    }

    try {
      await assignmentAPI.deleteQuizQuestion(
        assignment._id,
        language,
        stepNumber,
        questionIndex
      );
      await fetchAssignment();
      alert('Quiz question deleted successfully');
    } catch (error) {
      console.error('Error deleting quiz question:', error);
      alert('Failed to delete quiz question: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdateQuizQuestion = (questionIndex, field, value) => {
    const updatedQuestions = [...editingQuiz.questions];
    updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], [field]: value };
    setEditingQuiz({ ...editingQuiz, questions: updatedQuestions });
  };

  const handleUpdateQuizOption = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...editingQuiz.questions];
    const updatedOptions = [...updatedQuestions[questionIndex].options];
    updatedOptions[optionIndex] = value;
    updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], options: updatedOptions };
    setEditingQuiz({ ...editingQuiz, questions: updatedQuestions });
  };

  const handleAddQuizQuestion = () => {
    const newQuestion = {
      type: 'mcq',
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: ''
    };
    setEditingQuiz({
      ...editingQuiz,
      questions: [...editingQuiz.questions, newQuestion]
    });
  };

  const handleRemoveQuizQuestion = (questionIndex) => {
    setEditingQuiz({
      ...editingQuiz,
      questions: editingQuiz.questions.filter((_, idx) => idx !== questionIndex)
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading assignment details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <div className="error">{error}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/admin/coding-assignments')}>
            ← Back to Assignments
          </button>
        </div>
      </div>
    );
  }

  if (!assignment) return null;

  const levelConfig = developerLevelLabels[assignment.developerLevel];

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/admin/coding-assignments')}
              style={{ fontSize: '12px' }}
            >
              ← Back to Assignments
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate(`/assignment-submissions/${assignment._id}`)}
              style={{ fontSize: '12px', backgroundColor: '#48bb78', border: 'none' }}
            >
              📊 View Submissions
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleViewAIQuestions}
              style={{ fontSize: '12px', backgroundColor: '#667eea', border: 'none' }}
            >
              💬 AI Questions
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleViewGroups}
              style={{ fontSize: '12px', backgroundColor: '#f59e0b', border: 'none' }}
            >
              👥 View Groups
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
                {assignment.title}
              </h2>
              <div style={{ color: '#718096', fontSize: '13px', marginBottom: '12px' }}>
                <FormattedText text={assignment.description} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <div style={{
                  padding: '6px 12px',
                  backgroundColor: levelConfig.color,
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  {levelConfig.icon} {levelConfig.label}
                </div>
                <div style={{ color: '#718096', fontSize: '14px' }}>
                  Complexity: <span style={{ fontWeight: 'bold', color: '#2d3748' }}>{assignment.complexity}</span>
                </div>
                <div style={{ color: '#718096', fontSize: '14px' }}>
                  Code: <span style={{ fontWeight: 'bold', color: '#667eea' }}>{assignment.assignmentCode}</span>
                </div>
                <div style={{ color: '#718096', fontSize: '14px' }}>
                  Est. Time: <span style={{ fontWeight: 'bold', color: '#2d3748' }}>{assignment.estimatedTime} min</span>
                </div>
                {assignment.tags && assignment.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {assignment.tags.map((tag, index) => (
                      <span key={index} style={{ 
                        padding: '4px 10px',
                        background: '#f0fff4',
                        color: '#2f855a',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '500',
                        border: '1px solid #9ae6b4'
                      }}>
                        🏷️ {tag.name || tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-primary"
                onClick={handleEdit}
                style={{ fontSize: '12px', backgroundColor: '#fbbf24' }}
              >
                ✏️ Edit
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                style={{ fontSize: '12px' }}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>

        {/* Languages */}
        <div style={{ marginBottom: '24px', padding: '15px', background: '#f7fafc', borderRadius: '8px', border: '1.5px solid #e2e8f0' }}>
          <h3 style={{ color: '#2d3748', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
            Programming Languages
          </h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {assignment.languages?.map((lang, idx) => (
              <span
                key={idx}
                style={{
                  padding: '4px 12px',
                  background: 'white',
                  color: '#667eea',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: '1px solid #c7d2fe',
                  textTransform: 'uppercase'
                }}
              >
                {lang}
              </span>
            ))}
          </div>
        </div>

        {/* Problem Statement */}
        <div style={{ marginBottom: '24px', padding: '15px', background: '#fef3c7', borderRadius: '8px', border: '1.5px solid #fbbf24' }}>
          <h3 style={{ color: '#92400e', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
            📋 Problem Statement
          </h3>
          <div style={{ color: '#78350f', fontSize: '13px', lineHeight: '1.6' }}>
            <FormattedText text={assignment.problemStatement} />
          </div>
        </div>

        {/* Pseudo Code (Admin Only) */}
        {isAdmin && assignment.pseudocodeByLanguage && Object.keys(assignment.pseudocodeByLanguage).length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: '#8b5cf6', fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
              📝 Pseudo Code (Admin View)
            </h3>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              {assignment.languages?.map((lang) => {
                const pseudocode = assignment.pseudocodeByLanguage?.[lang];
                if (!pseudocode || pseudocode.trim() === '') return null;
                
                const isEditing = editingPseudocode?.language === lang;
                
                return (
                  <div 
                    key={lang}
                    style={{
                      border: '1.5px solid #c7d2fe',
                      borderRadius: '12px',
                      padding: '18px',
                      background: '#f5f3ff'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '12px'
                    }}>
                      <span style={{
                        padding: '4px 12px',
                        background: '#8b5cf6',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {lang}
                      </span>
                      
                      {/* Edit/Save/Cancel Buttons */}
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={async () => {
                              try {
                                setSavingPseudocode(true);
                                const response = await assignmentAPI.updatePseudocode(
                                  assignment._id,
                                  lang,
                                  editingPseudocode.text
                                );
                                // Update the assignment state with new pseudocode
                                setAssignment({
                                  ...assignment,
                                  pseudocodeByLanguage: response.data.pseudocodeByLanguage
                                });
                                setEditingPseudocode(null);
                                alert('Pseudocode updated successfully!');
                              } catch (err) {
                                alert(err.response?.data?.message || 'Failed to update pseudocode');
                              } finally {
                                setSavingPseudocode(false);
                              }
                            }}
                            disabled={savingPseudocode}
                            style={{
                              padding: '6px 14px',
                              backgroundColor: '#48bb78',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: savingPseudocode ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                              opacity: savingPseudocode ? 0.6 : 1
                            }}
                          >
                            {savingPseudocode ? '💾 Saving...' : '💾 Save'}
                          </button>
                          <button
                            onClick={() => setEditingPseudocode(null)}
                            disabled={savingPseudocode}
                            style={{
                              padding: '6px 14px',
                              backgroundColor: '#e2e8f0',
                              color: '#4a5568',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: savingPseudocode ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                          >
                            ❌ Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingPseudocode({ language: lang, text: pseudocode })}
                          style={{
                            padding: '6px 14px',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#7c3aed'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#8b5cf6'}
                        >
                          ✏️ Edit
                        </button>
                      )}
                    </div>
                    
                    {/* Display or Edit Mode */}
                    {isEditing ? (
                      <textarea
                        value={editingPseudocode.text}
                        onChange={(e) => setEditingPseudocode({ ...editingPseudocode, text: e.target.value })}
                        style={{
                          width: '100%',
                          minHeight: '300px',
                          padding: '12px',
                          border: '2px solid #8b5cf6',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontFamily: 'Monaco, Courier, monospace',
                          lineHeight: '1.6',
                          resize: 'vertical',
                          backgroundColor: '#faf5ff',
                          color: '#5b21b6'
                        }}
                        placeholder="Enter pseudocode..."
                      />
                    ) : (
                      <div style={{ 
                        background: '#faf5ff',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid #e9d5ff'
                      }}>
                        <pre style={{ 
                          margin: 0,
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          color: '#5b21b6',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {pseudocode}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Algorithm Steps & Hints */}
        <div>
          <h3 style={{ color: '#667eea', fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
            🔑 Algorithm Steps & Hints
          </h3>
          
          {/* Language Tabs */}
          {assignment.languages && assignment.languages.length > 0 && (
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {assignment.languages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      borderBottom: selectedLanguage === lang ? '3px solid #667eea' : '3px solid transparent',
                      background: selectedLanguage === lang ? '#eef2ff' : 'transparent',
                      color: selectedLanguage === lang ? '#667eea' : '#718096',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: selectedLanguage === lang ? '600' : '500',
                      textTransform: 'capitalize',
                      transition: 'all 0.2s',
                      borderRadius: '6px 6px 0 0',
                      marginBottom: '-2px'
                    }}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Steps for selected language */}
          {selectedLanguage && assignment.algorithmByLanguage?.[selectedLanguage]?.steps && (
            <div style={{ display: 'grid', gap: '15px' }}>
              {assignment.algorithmByLanguage[selectedLanguage].steps.map((step, idx) => (
                <div 
                  key={idx}
                  style={{
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '18px',
                    background: '#fafafa',
                    position: 'relative'
                  }}
                >
                  {/* Action Buttons */}
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      onClick={() => handleEditStep(step, idx)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteStep(idx)}
                      disabled={saving}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        opacity: saving ? 0.6 : 1
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                  
                  <div style={{ marginBottom: '12px', paddingRight: '160px' }}>
                    <h4 style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                      Step {step.stepNumber}: {step.title}
                    </h4>
                    <div style={{ color: '#4a5568', fontSize: '13px', lineHeight: '1.5' }}>
                      <FormattedText text={step.description} />
                    </div>
                  </div>

                  {/* Starter Code */}
                  {step.starterCode && step.starterCode.trim() !== '' && (
                    <div style={{ marginBottom: '12px' }}>
                      <h5 style={{ color: '#667eea', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                        🚀 Starter Code:
                      </h5>
                      <FormattedText text={`\`\`\`${selectedLanguage}\n${step.starterCode}\n\`\`\``} />
                    </div>
                  )}
                  
                  {/* Hints for this step */}
                  {step.hints && step.hints.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <h5 style={{ color: '#667eea', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                        💡 Hints ({step.hints.length}):
                      </h5>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {step.hints.map((hint, hintIdx) => (
                          <div 
                            key={hintIdx}
                            style={{
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              padding: '10px',
                              fontSize: '12px',
                              color: '#4a5568'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div style={{ flex: 1 }}>
                                <strong style={{ color: '#667eea' }}>Hint {hintIdx + 1}:</strong>{' '}
                                <FormattedText text={hint.text} />
                              </div>
                              {hint.pointPenalty > 0 && (
                                <span style={{ 
                                  marginLeft: '8px', 
                                  color: '#ef4444', 
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap'
                                }}>
                                  (-{hint.pointPenalty} pts)
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quiz Questions for this step */}
                  {assignment.quizzesByLanguage?.[selectedLanguage]?.[step.stepNumber] && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h5 style={{ color: '#f59e0b', fontSize: '13px', fontWeight: '600', margin: 0 }}>
                          🧠 Quiz Questions ({assignment.quizzesByLanguage[selectedLanguage][step.stepNumber].length}):
                        </h5>
                        <button
                          onClick={() => handleEditQuiz(selectedLanguage, step.stepNumber)}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Edit Quiz
                        </button>
                      </div>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {assignment.quizzesByLanguage[selectedLanguage][step.stepNumber].map((question, qIdx) => (
                          <div 
                            key={qIdx}
                            style={{
                              background: 'white',
                              border: '1px solid #fbbf24',
                              borderRadius: '6px',
                              padding: '12px',
                              fontSize: '12px',
                              color: '#4a5568'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                  <span style={{ 
                                    backgroundColor: '#f59e0b', 
                                    color: 'white', 
                                    padding: '2px 6px', 
                                    borderRadius: '4px', 
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>
                                    Q{qIdx + 1}
                                  </span>
                                  <span style={{ 
                                    backgroundColor: question.type === 'mcq' ? '#3b82f6' : '#10b981', 
                                    color: 'white', 
                                    padding: '2px 6px', 
                                    borderRadius: '4px', 
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>
                                    {question.type === 'mcq' ? 'MCQ' : 'True/False'}
                                  </span>
                                  <ReportIssueButton
                                    sourceType="assignment"
                                    sourceId={assignment._id}
                                    questionPath={`quizzesByLanguage.${selectedLanguage}.${step.stepNumber}.${qIdx}`}
                                    questionId={`${assignment._id}-${selectedLanguage}-${step.stepNumber}-${qIdx}`}
                                    questionIndex={qIdx}
                                    questionData={{
                                      question: question.question,
                                      options: question.options,
                                      correctAnswer: question.correctAnswer,
                                      questionType: question.type,
                                      explanation: question.explanation,
                                      difficulty: question.difficulty,
                                      tags: question.tags
                                    }}
                                    metadata={{
                                      sourceName: assignment.title,
                                      sourceCode: assignment.code,
                                      language: selectedLanguage,
                                      stepNumber: step.stepNumber,
                                      stepTitle: step.title,
                                      questionNumber: qIdx + 1,
                                      totalQuestions: step.quiz?.length || 0,
                                      reportedFrom: 'assignment-admin-details',
                                      timestamp: new Date().toISOString(),
                                      groupName: assignment.group?.name,
                                      groupId: assignment.group?._id
                                    }}
                                    size="icon-only"
                                  />
                                </div>
                                <div style={{ color: '#2d3748', fontWeight: '600', marginBottom: '8px' }}>{question.question}</div>
                                <div style={{ marginLeft: '12px', marginBottom: '8px' }}>
                                  {question.options.map((option, optIdx) => (
                                    <div 
                                      key={optIdx} 
                                      style={{ 
                                        marginBottom: '4px',
                                        color: optIdx === question.correctAnswer ? '#10b981' : '#4a5568',
                                        fontWeight: optIdx === question.correctAnswer ? '600' : 'normal'
                                      }}
                                    >
                                      {optIdx === question.correctAnswer ? '✓ ' : '• '}{option}
                                    </div>
                                  ))}
                                </div>
                                <div style={{ 
                                  backgroundColor: '#f0fdf4', 
                                  border: '1px solid #86efac', 
                                  borderRadius: '4px', 
                                  padding: '8px',
                                  marginTop: '8px'
                                }}>
                                  <strong style={{ color: '#166534', fontSize: '11px' }}>Explanation:</strong>{' '}
                                  <span style={{ color: '#166534', fontSize: '11px' }}>{question.explanation}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteQuizQuestion(selectedLanguage, step.stepNumber, qIdx)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  marginLeft: '12px',
                                  flexShrink: 0
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Fallback for old data structure */}
          {!assignment.algorithmByLanguage && assignment.steps && (
            <div style={{ display: 'grid', gap: '15px' }}>
              {assignment.steps.map((step, idx) => (
                <div 
                  key={idx}
                  style={{
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '18px',
                    background: '#fafafa'
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <h4 style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                      Step {step.stepNumber}: {step.title}
                    </h4>
                    <div style={{ color: '#4a5568', fontSize: '13px', lineHeight: '1.5' }}>
                      <FormattedText text={step.description} />
                    </div>
                  </div>

                  {/* Starter Code */}
                  {step.starterCode && Object.keys(step.starterCode).length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <h5 style={{ color: '#667eea', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                        🚀 Starter Code:
                      </h5>
                      {Object.entries(step.starterCode).map(([lang, code]) => (
                        <div key={lang} style={{ marginBottom: '8px' }}>
                          <FormattedText text={`\`\`\`${lang}\n${code}\n\`\`\``} />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Hints for this step */}
                  {step.hints && step.hints.length > 0 && (
                    <div>
                      <h5 style={{ color: '#667eea', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
                        💡 Hints ({step.hints.length}):
                      </h5>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {step.hints.map((hint, hintIdx) => (
                          <div 
                            key={hintIdx}
                            style={{
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              padding: '10px',
                              fontSize: '12px',
                              color: '#4a5568'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div style={{ flex: 1 }}>
                                <strong style={{ color: '#667eea' }}>Hint {hintIdx + 1}:</strong>{' '}
                                <FormattedText text={hint.text} />
                              </div>
                              {hint.pointPenalty > 0 && (
                                <span style={{ 
                                  marginLeft: '8px', 
                                  color: '#ef4444', 
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap'
                                }}>
                                  (-{hint.pointPenalty} pts)
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Statistics */}
        <div style={{ 
          marginTop: '24px', 
          padding: '15px', 
          background: '#f0fdf4', 
          borderRadius: '8px', 
          border: '1.5px solid #86efac'
        }}>
          <h3 style={{ color: '#166534', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
            📊 Statistics
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            <div>
              <div style={{ color: '#166534', fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>
                Total Steps
              </div>
              <div style={{ color: '#166534', fontSize: '20px', fontWeight: '700' }}>
                {assignment.steps?.length || 0}
              </div>
            </div>
            <div>
              <div style={{ color: '#166534', fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>
                Total Attempts
              </div>
              <div style={{ color: '#166534', fontSize: '20px', fontWeight: '700' }}>
                {assignment.submissionCount || 0}
              </div>
            </div>
            <div>
              <div style={{ color: '#166534', fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>
                Completed
              </div>
              <div style={{ color: '#166534', fontSize: '20px', fontWeight: '700' }}>
                {assignment.completedCount || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Step Modal */}
      {showEditModal && editingStep && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '30px'
          }}>
            <h2 style={{ color: '#2d3748', marginBottom: '20px', fontSize: '20px' }}>
              ✏️ Edit Step {editingStep.stepNumber} ({editingStep.language})
            </h2>

            {/* Step Number */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>
                Step Number
              </label>
              <input
                type="number"
                value={editingStep.stepNumber}
                onChange={(e) => setEditingStep({ ...editingStep, stepNumber: parseInt(e.target.value) })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Title */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>
                Title
              </label>
              <input
                type="text"
                value={editingStep.title}
                onChange={(e) => setEditingStep({ ...editingStep, title: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>
                Description
              </label>
              <textarea
                value={editingStep.description}
                onChange={(e) => setEditingStep({ ...editingStep, description: e.target.value })}
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Starter Code */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>
                Starter Code (with Markdown syntax)
              </label>
              <div style={{ fontSize: '11px', color: '#718096', marginBottom: '5px' }}>
                Use markdown code fence syntax: ```language then code then ```
              </div>
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <Editor
                  height="300px"
                  defaultLanguage="markdown"
                  value={editingStep.starterCodeMarkdown || ''}
                  onChange={(value) => setEditingStep({ ...editingStep, starterCodeMarkdown: value || '' })}
                  theme="vs-light"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 16,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    wordWrap: 'on',
                    bracketPairColorization: { enabled: true },
                    autoClosingBrackets: 'always',
                    autoClosingQuotes: 'always',
                    autoIndent: 'full',
                    formatOnPaste: true,
                    formatOnType: true
                  }}
                />
              </div>
              
              {/* Live Preview */}
              {editingStep.starterCodeMarkdown && editingStep.starterCodeMarkdown.trim() && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '11px', color: '#718096', marginBottom: '5px', fontWeight: '600' }}>
                    Preview:
                  </div>
                  <div style={{
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    backgroundColor: 'white'
                  }}>
                    <FormattedText text={editingStep.starterCodeMarkdown} />
                  </div>
                </div>
              )}
            </div>

            {/* Hints */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>
                  Hints ({editingStep.hints?.length || 0})
                </label>
                <button
                  onClick={handleAddHint}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#48bb78',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  + Add Hint
                </button>
              </div>

              {editingStep.hints?.map((hint, hintIdx) => (
                <div key={hintIdx} style={{
                  padding: '15px',
                  marginBottom: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  backgroundColor: '#f7fafc'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '13px' }}>
                      Hint {hintIdx + 1}
                    </span>
                    <button
                      onClick={() => handleRemoveHint(hintIdx)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  <textarea
                    value={hint.text}
                    onChange={(e) => handleUpdateHint(hintIdx, 'text', e.target.value)}
                    placeholder="Hint text..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      marginBottom: '8px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#718096', marginBottom: '3px' }}>
                        Point Penalty
                      </label>
                      <input
                        type="number"
                        value={hint.pointPenalty}
                        onChange={(e) => handleUpdateHint(hintIdx, 'pointPenalty', parseInt(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '6px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '11px', color: '#718096', marginBottom: '3px' }}>
                        <input
                          type="checkbox"
                          checked={hint.allowFollowUp}
                          onChange={(e) => handleUpdateHint(hintIdx, 'allowFollowUp', e.target.checked)}
                          style={{ marginRight: '5px' }}
                        />
                        Allow Follow-up
                      </label>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', color: '#718096', marginBottom: '3px' }}>
                        Max Follow-ups
                      </label>
                      <input
                        type="number"
                        value={hint.maxFollowUps}
                        onChange={(e) => handleUpdateHint(hintIdx, 'maxFollowUps', parseInt(e.target.value) || 1)}
                        disabled={!hint.allowFollowUp}
                        min="1"
                        max="2"
                        style={{
                          width: '100%',
                          padding: '6px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          fontSize: '12px',
                          opacity: hint.allowFollowUp ? 1 : 0.5
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingStep(null);
                }}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#e2e8f0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStep}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Edit Modal */}
      {showQuizEditModal && editingQuiz && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          overflowY: 'auto',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#2d3748', marginBottom: '20px', fontSize: '22px', fontWeight: 'bold' }}>
              🧠 Edit Quiz Questions - Step {editingQuiz.stepNumber}
            </h3>

            <div style={{ marginBottom: '20px' }}>
              {editingQuiz.questions.length === 0 ? (
                <div style={{ 
                  padding: '30px', 
                  textAlign: 'center', 
                  backgroundColor: '#f7fafc', 
                  borderRadius: '8px',
                  color: '#718096'
                }}>
                  <p>No quiz questions yet. Click "Add Question" to create one.</p>
                </div>
              ) : (
                editingQuiz.questions.map((question, qIdx) => (
                  <div key={qIdx} style={{
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '20px',
                    backgroundColor: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h4 style={{ color: '#f59e0b', fontSize: '16px', fontWeight: 'bold', margin: 0 }}>
                        Question {qIdx + 1}
                      </h4>
                      <button
                        onClick={() => handleRemoveQuizQuestion(qIdx)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ Delete Question
                      </button>
                    </div>

                    {/* Question Type */}
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                        Question Type
                      </label>
                      <select
                        value={question.type}
                        onChange={(e) => {
                          const newType = e.target.value;
                          handleUpdateQuizQuestion(qIdx, 'type', newType);
                          // If changing to true/false, update options
                          if (newType === 'boolean' && question.options.length !== 2) {
                            handleUpdateQuizQuestion(qIdx, 'options', ['True', 'False']);
                            handleUpdateQuizQuestion(qIdx, 'correctAnswer', 0);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="mcq">Multiple Choice (MCQ)</option>
                        <option value="boolean">True/False</option>
                      </select>
                    </div>

                    {/* Question Text */}
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                        Question
                      </label>
                      <textarea
                        value={question.question}
                        onChange={(e) => handleUpdateQuizQuestion(qIdx, 'question', e.target.value)}
                        rows={3}
                        placeholder="Enter your question..."
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    {/* Options */}
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                        Options
                      </label>
                      {question.type === 'boolean' ? (
                        <div style={{ display: 'grid', gap: '10px' }}>
                          {['True', 'False'].map((opt, optIdx) => (
                            <div key={optIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <input
                                type="radio"
                                name={`correct-${qIdx}`}
                                checked={question.correctAnswer === optIdx}
                                onChange={() => handleUpdateQuizQuestion(qIdx, 'correctAnswer', optIdx)}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ color: '#2d3748', fontWeight: question.correctAnswer === optIdx ? '600' : 'normal' }}>
                                {opt}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                          {question.options.map((option, optIdx) => (
                            <div key={optIdx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <input
                                type="radio"
                                name={`correct-${qIdx}`}
                                checked={question.correctAnswer === optIdx}
                                onChange={() => handleUpdateQuizQuestion(qIdx, 'correctAnswer', optIdx)}
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                              />
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => handleUpdateQuizOption(qIdx, optIdx, e.target.value)}
                                placeholder={`Option ${optIdx + 1}`}
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: '#718096', marginTop: '6px' }}>
                        Select the radio button next to the correct answer
                      </div>
                    </div>

                    {/* Explanation */}
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                        Explanation
                      </label>
                      <textarea
                        value={question.explanation}
                        onChange={(e) => handleUpdateQuizQuestion(qIdx, 'explanation', e.target.value)}
                        rows={2}
                        placeholder="Explain why this is the correct answer..."
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add Question Button */}
            <button
              onClick={handleAddQuizQuestion}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginBottom: '20px'
              }}
            >
              ➕ Add Question
            </button>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowQuizEditModal(false);
                  setEditingQuiz(null);
                }}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#e2e8f0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveQuiz}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Quiz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Questions Modal */}
      {showAIQuestionsModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setShowAIQuestionsModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '16px' }}>
              <h3 style={{ color: '#667eea', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
                💬 AI Chatbot Questions
              </h3>
              {loadingAIQuestions ? (
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{
                      width: '160px',
                      height: '54px',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '8px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: `${i * 0.15}s`
                    }} />
                  ))}
                  <style>{`
                    @keyframes pulse {
                      0%, 100% { opacity: 1; }
                      50% { opacity: 0.5; }
                    }
                  `}</style>
                </div>
              ) : aiQuestions ? (
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ padding: '8px 16px', backgroundColor: '#eef2ff', borderRadius: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '600' }}>Total Questions:</span>
                    <span style={{ fontSize: '18px', color: '#667eea', fontWeight: 'bold', marginLeft: '8px' }}>
                      {aiQuestions.stats.totalQuestions}
                    </span>
                  </div>
                  <div style={{ padding: '8px 16px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>Students:</span>
                    <span style={{ fontSize: '18px', color: '#16a34a', fontWeight: 'bold', marginLeft: '8px' }}>
                      {aiQuestions.stats.totalStudents}
                    </span>
                  </div>
                  <div style={{ padding: '8px 16px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#ca8a04', fontWeight: '600' }}>Avg per Student:</span>
                    <span style={{ fontSize: '18px', color: '#ca8a04', fontWeight: 'bold', marginLeft: '8px' }}>
                      {aiQuestions.stats.averageQuestionsPerStudent}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {loadingAIQuestions ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                Loading AI questions...
              </div>
            ) : aiQuestions && aiQuestions.questionsByStudent.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {aiQuestions.questionsByStudent.map((studentData, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '16px',
                      backgroundColor: '#f9fafb'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748', marginBottom: '4px' }}>
                          {studentData.student.name}
                        </h4>
                        <p style={{ fontSize: '13px', color: '#718096', margin: 0 }}>
                          {studentData.student.email}
                        </p>
                      </div>
                      <div style={{
                        padding: '6px 12px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        {studentData.totalAsked} question{studentData.totalAsked !== 1 ? 's' : ''}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {studentData.questions.map((q, qIdx) => (
                        <div
                          key={q._id}
                          style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '700',
                              color: '#667eea',
                              padding: '2px 8px',
                              backgroundColor: '#eef2ff',
                              borderRadius: '4px'
                            }}>
                              Q{qIdx + 1}
                            </span>
                            {q.step && (
                              <span style={{
                                fontSize: '11px',
                                color: '#718096',
                                backgroundColor: '#f3f4f6',
                                padding: '2px 8px',
                                borderRadius: '4px'
                              }}>
                                Step {q.step}
                              </span>
                            )}
                            {q.language && (
                              <span style={{
                                fontSize: '11px',
                                color: '#059669',
                                backgroundColor: '#d1fae5',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                textTransform: 'uppercase'
                              }}>
                                {q.language}
                              </span>
                            )}
                            {q.wasSuggested && (
                              <span style={{
                                fontSize: '11px',
                                color: '#f59e0b',
                                backgroundColor: '#fef3c7',
                                padding: '2px 8px',
                                borderRadius: '4px'
                              }}>
                                Suggested
                              </span>
                            )}
                            <span style={{ fontSize: '11px', color: '#a0aec0', marginLeft: 'auto' }}>
                              {new Date(q.askedAt).toLocaleString()}
                            </span>
                          </div>
                          <p style={{ fontSize: '14px', color: '#2d3748', margin: 0, lineHeight: '1.5', fontWeight: '600' }}>
                            {q.question}
                          </p>
                          {q.aiResponse && (
                            <div style={{
                              marginTop: '10px',
                              padding: '10px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '6px',
                              border: '1px solid #86efac'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                <span style={{ fontSize: '13px' }}>🤖</span>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#16a34a' }}>AI Response:</span>
                              </div>
                              <div style={{
                                fontSize: '13px',
                                color: '#166534',
                                lineHeight: '1.6'
                              }}>
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    // Style code blocks
                                    code: ({node, inline, className, children, ...props}) => {
                                      return inline ? (
                                        <code style={{
                                          backgroundColor: '#d1fae5',
                                          padding: '2px 6px',
                                          borderRadius: '3px',
                                          fontSize: '12px',
                                          fontFamily: 'monospace'
                                        }} {...props}>
                                          {children}
                                        </code>
                                      ) : (
                                        <pre style={{
                                          backgroundColor: '#dcfce7',
                                          padding: '10px',
                                          borderRadius: '6px',
                                          overflow: 'auto',
                                          marginTop: '6px',
                                          marginBottom: '6px'
                                        }}>
                                          <code style={{
                                            fontFamily: 'monospace',
                                            fontSize: '12px'
                                          }} {...props}>
                                            {children}
                                          </code>
                                        </pre>
                                      );
                                    },
                                    // Style paragraphs
                                    p: ({children}) => (
                                      <p style={{ margin: '6px 0' }}>{children}</p>
                                    ),
                                    // Style lists
                                    ul: ({children}) => (
                                      <ul style={{ marginLeft: '20px', marginTop: '6px', marginBottom: '6px' }}>{children}</ul>
                                    ),
                                    ol: ({children}) => (
                                      <ol style={{ marginLeft: '20px', marginTop: '6px', marginBottom: '6px' }}>{children}</ol>
                                    )
                                  }}
                                >
                                  {q.aiResponse}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}
                          {q.studentCode && (
                            <details style={{ marginTop: '8px' }}>
                              <summary style={{ fontSize: '12px', color: '#667eea', cursor: 'pointer', fontWeight: '600' }}>
                                View Code Snapshot
                              </summary>
                              <pre style={{
                                marginTop: '8px',
                                padding: '12px',
                                backgroundColor: '#1e293b',
                                color: '#e2e8f0',
                                borderRadius: '6px',
                                fontSize: '12px',
                                overflow: 'auto',
                                maxHeight: '200px'
                              }}>
                                {q.studentCode}
                              </pre>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>No AI questions asked yet</p>
                <p style={{ fontSize: '13px' }}>Students haven't used the AI assistant for this assignment yet.</p>
              </div>
            )}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAIQuestionsModal(false)}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Groups Modal */}
      {showGroupsModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setShowGroupsModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '16px' }}>
              <h3 style={{ color: '#f59e0b', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
                👥 Groups with Access
              </h3>
              {loadingGroups ? (
                <p style={{ color: '#718096', fontSize: '14px' }}>Loading groups...</p>
              ) : assignmentGroups ? (
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ padding: '8px 16px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600' }}>Code Repos:</span>
                    <span style={{ fontSize: '18px', color: '#f59e0b', fontWeight: 'bold', marginLeft: '8px' }}>
                      {assignmentGroups.stats.totalCodeRepos}
                    </span>
                  </div>
                  <div style={{ padding: '8px 16px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>Total Groups:</span>
                    <span style={{ fontSize: '18px', color: '#16a34a', fontWeight: 'bold', marginLeft: '8px' }}>
                      {assignmentGroups.stats.totalGroups}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {loadingGroups ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                Loading groups...
              </div>
            ) : assignmentGroups ? (
              <div>
                {/* Code Repos Section */}
                {assignmentGroups.codeRepos && assignmentGroups.codeRepos.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>
                      📦 Code Repositories
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {assignmentGroups.codeRepos.map((repo, idx) => (
                        <div
                          key={repo._id}
                          style={{
                            padding: '12px 16px',
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                              {repo.title}
                            </span>
                            <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                              ({repo.repoCode})
                            </span>
                          </div>
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#eef2ff',
                            color: '#667eea',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {repo.groupCount} group{repo.groupCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Groups Section */}
                {assignmentGroups.groups && assignmentGroups.groups.length > 0 ? (
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>
                      👥 Groups
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {assignmentGroups.groups.map((group, idx) => (
                        <div
                          key={group._id}
                          style={{
                            padding: '16px',
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: '8px'
                          }}
                        >
                          <h5 style={{ fontSize: '15px', fontWeight: '600', color: '#16a34a', marginBottom: '4px' }}>
                            {group.name}
                          </h5>
                          {group.description && (
                            <p style={{ fontSize: '13px', color: '#718096', margin: 0 }}>
                              {group.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>No groups found</p>
                    <p style={{ fontSize: '13px' }}>This assignment hasn't been shared with any groups yet.</p>
                  </div>
                )}
              </div>
            ) : null}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowGroupsModal(false)}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentDetails;
